import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  useMemo,
} from "react";
import { SpreadsheetHighlightConfig } from "../../data_room/data_room_file/FileContent";
import {
  columnIndexToLetter,
  COLUMN_INDEX_OPTIONS,
} from "../../../utils/columnConversion";

// Column configuration colors
export const COLUMN_COLORS = {
  section: { solid: "#3B82F6", light: "#DBEAFE" }, // Blue
  subsection: { solid: "#10B981", light: "#D1FAE5" }, // Green
  question: { solid: "#8B5CF6", light: "#EDE9FE" }, // Purple
} as const;

// Column options as 0-indexed integers (0-25 for A-Z)
export const COLUMN_OPTIONS = COLUMN_INDEX_OPTIONS;

// Row options 1-100
export const ROW_OPTIONS = Array.from({ length: 100 }, (_, i) => i + 1);

export type ColumnConfig = {
  sectionColumn: number | null;
  subsectionColumn: number | null;
  questionColumn: number | null;
  startRow: number;
};

type DdqlImportConfigContextType = {
  // Column configuration
  sectionColumn: number | null;
  setSectionColumn: Dispatch<SetStateAction<number | null>>;
  subsectionColumn: number | null;
  setSubsectionColumn: Dispatch<SetStateAction<number | null>>;
  questionColumn: number | null;
  setQuestionColumn: Dispatch<SetStateAction<number | null>>;
  startRow: number;
  setStartRow: Dispatch<SetStateAction<number>>;

  // Template default columns (for detecting overrides)
  templateSectionColumn: number | null;
  setTemplateSectionColumn: Dispatch<SetStateAction<number | null>>;
  templateQuestionColumn: number | null;
  setTemplateQuestionColumn: Dispatch<SetStateAction<number | null>>;

  // Template selection
  selectedTemplateId: string;
  setSelectedTemplateId: Dispatch<SetStateAction<string>>;

  // Skipped sheets
  skippedSheetIndexes: number[];
  setSkippedSheetIndexes: Dispatch<SetStateAction<number[]>>;
  toggleSkippedSheet: (sheetIndex: number) => void;

  // Computed values
  highlightConfig: SpreadsheetHighlightConfig;

  // Reset function
  resetConfig: () => void;
};

const DdqlImportConfigContext = createContext<
  DdqlImportConfigContextType | undefined
>(undefined);

const DEFAULT_SECTION_COLUMN: number | null = null; // null = no column selected
const DEFAULT_SUBSECTION_COLUMN: number | null = null; // null = no subsection
const DEFAULT_QUESTION_COLUMN: number | null = null; // null = no column selected
const DEFAULT_START_ROW = 0;

export function DdqlImportConfigProvider({
  children,
  initialTemplateId = "",
}: {
  children: React.ReactNode;
  initialTemplateId?: string;
}) {
  // Column configuration state
  const [sectionColumn, setSectionColumn] = useState<number | null>(DEFAULT_SECTION_COLUMN);
  const [subsectionColumn, setSubsectionColumn] = useState<number | null>(
    DEFAULT_SUBSECTION_COLUMN,
  );
  const [questionColumn, setQuestionColumn] = useState<number | null>(DEFAULT_QUESTION_COLUMN);
  const [startRow, setStartRow] = useState(DEFAULT_START_ROW);

  // Template default columns (for detecting overrides)
  const [templateSectionColumn, setTemplateSectionColumn] = useState<number | null>(null);
  const [templateQuestionColumn, setTemplateQuestionColumn] = useState<number | null>(null);

  // Template selection state
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(initialTemplateId);

  // Skipped sheets state
  const [skippedSheetIndexes, setSkippedSheetIndexes] = useState<number[]>([]);

  // Toggle a sheet's skipped status
  const toggleSkippedSheet = (sheetIndex: number) => {
    setSkippedSheetIndexes((prev) =>
      prev.includes(sheetIndex)
        ? prev.filter((idx) => idx !== sheetIndex)
        : [...prev, sheetIndex],
    );
  };

  // Build highlight config for spreadsheet (filter out empty columns)
  // Convert column indices to letters for the spreadsheet component
  const highlightConfig: SpreadsheetHighlightConfig = useMemo(
    () => ({
      columns: [
        ...(sectionColumn !== null
          ? [{ column: columnIndexToLetter(sectionColumn), color: COLUMN_COLORS.section.light }]
          : []),
        ...(subsectionColumn !== null
          ? [{ column: columnIndexToLetter(subsectionColumn), color: COLUMN_COLORS.subsection.light }]
          : []),
        ...(questionColumn !== null
          ? [{ column: columnIndexToLetter(questionColumn), color: COLUMN_COLORS.question.light }]
          : []),
      ],
      startRow,
    }),
    [sectionColumn, subsectionColumn, questionColumn, startRow],
  );

  // Reset all configuration to defaults
  const resetConfig = () => {
    setSectionColumn(DEFAULT_SECTION_COLUMN);
    setSubsectionColumn(DEFAULT_SUBSECTION_COLUMN);
    setQuestionColumn(DEFAULT_QUESTION_COLUMN);
    setStartRow(DEFAULT_START_ROW);
    setSelectedTemplateId(initialTemplateId);
    setSkippedSheetIndexes([]);
    setTemplateSectionColumn(null);
    setTemplateQuestionColumn(null);
  };

  return (
    <DdqlImportConfigContext.Provider
      value={{
        sectionColumn,
        setSectionColumn,
        subsectionColumn,
        setSubsectionColumn,
        questionColumn,
        setQuestionColumn,
        startRow,
        setStartRow,
        templateSectionColumn,
        setTemplateSectionColumn,
        templateQuestionColumn,
        setTemplateQuestionColumn,
        selectedTemplateId,
        setSelectedTemplateId,
        skippedSheetIndexes,
        setSkippedSheetIndexes,
        toggleSkippedSheet,
        highlightConfig,
        resetConfig,
      }}
    >
      {children}
    </DdqlImportConfigContext.Provider>
  );
}

export const useDdqlImportConfig = () => {
  const context = useContext(DdqlImportConfigContext);
  if (!context) {
    throw new Error(
      "useDdqlImportConfig must be used within a DdqlImportConfigProvider",
    );
  }
  return context;
};
